import type { FilterType } from "enums/components";

import type { TechnicalConfigFieldKind } from "./fieldKindStrategies";

export type ActiveField<TField> = Exclude<TField, InactiveFieldDefinition>;

export type CatalogColumnFieldOf<TField> = Extract<
  TField,
  { catalogColumn: NonNullable<TField extends { catalogColumn?: infer C } ? C : never> }
>;

export type CatalogHeaderFieldOf<TField> = Extract<TField, { catalogHeader: true }>;
export type ConfigApiValueForKind<K extends TechnicalConfigFieldKind> = K extends "boolean"
  ? boolean
  : K extends "number" | "time"
  ? null | number
  : string;

export type ConfigFieldRef = {
  catalogColumn?: unknown;
  catalogHeader?: boolean;
  isFieldInactive?: boolean;
  key: string;
  tab?: null | string;
};

export type DefinitionByKey<
  TDefinitions extends readonly { key: string }[],
  TKey extends TDefinitions[number]["key"]
> = Extract<TDefinitions[number], { key: TKey }>;

export type FormStateFromFieldKeys<
  TDefinitions extends readonly { key: string; kind: string }[],
  TFieldKey extends TDefinitions[number]["key"] = TDefinitions[number]["key"]
> = {
  [K in TFieldKey]: FormValueForKind<Extract<TDefinitions[number], { key: K }>["kind"]>;
};

export type FormValueForKind<TKind extends string> = TKind extends "boolean" ? boolean : string;

export type HeaderFieldOf<TField extends ConfigFieldRef> = Exclude<TField, { tab: string }>;

export type InactiveFieldDefinition = { isFieldInactive: true };

export type TabFieldOf<TField, TTab extends string> = Extract<TField, { tab: TTab }>;

export type TechnicalConfigFieldDefinitionCore<TKind extends string, TTab extends string = string> = {
  colSpan?: 1 | 2;
  default: boolean | null | string;
  filterType: FilterType;
  inputType?: "number" | "string";
  isFieldInactive?: boolean;
  key: string;
  kind: TKind;
  labelKey?: string;
  options?: readonly (number | string)[];
  section?: string;
  tab?: TTab;
};

export const isActiveField = <TField extends ConfigFieldRef>(field: TField): field is ActiveField<TField> =>
  !("isFieldInactive" in field) || !field.isFieldInactive;

export const filterActiveFields = <TField extends ConfigFieldRef>(
  fields: readonly TField[]
): readonly ActiveField<TField>[] => fields.filter(isActiveField);

export const isTabField = <TField extends ConfigFieldRef>(field: TField): field is Extract<TField, { tab: string }> =>
  "tab" in field && field.tab != null;

export const filterTabFields = <TField extends ConfigFieldRef>(
  fields: readonly TField[]
): readonly Extract<TField, { tab: string }>[] => fields.filter(isTabField);

export const isHeaderField = <TField extends ConfigFieldRef>(field: TField): field is HeaderFieldOf<TField> =>
  !("tab" in field) || field.tab == null;

export const filterHeaderFields = <TField extends ConfigFieldRef>(
  fields: readonly TField[]
): readonly HeaderFieldOf<TField>[] => fields.filter(isHeaderField);

export const hasCatalogColumn = <TField extends ConfigFieldRef>(
  field: TField
): field is Extract<TField, { catalogColumn: NonNullable<TField["catalogColumn"]> }> => Boolean(field.catalogColumn);

export const filterCatalogColumnFields = <TField extends ConfigFieldRef>(
  fields: readonly TField[]
): readonly Extract<TField, { catalogColumn: NonNullable<TField["catalogColumn"]> }>[] =>
  fields.filter(hasCatalogColumn);

export const isCatalogHeaderField = <TField extends ConfigFieldRef>(
  field: TField
): field is Extract<TField, { catalogHeader: true }> => "catalogHeader" in field && field.catalogHeader === true;

export const filterCatalogHeaderFields = <TField extends ConfigFieldRef>(
  fields: readonly TField[]
): readonly Extract<TField, { catalogHeader: true }>[] => fields.filter(isCatalogHeaderField);
